"use client";

import { useCallback, useEffect, useRef } from "react";
import type { FilterClause } from "../../domain";
import { useRecentFilters } from "../use-recent-filters";

export interface UseFilterHistoryParams {
  filters: FilterClause[];
  onFiltersChange: (filters: FilterClause[]) => void;
}

/** Стек отмены фильтров (до 50 шагов, Ctrl/Cmd+Z) и запись недавних фильтров. */
export function useFilterHistory({ filters, onFiltersChange }: UseFilterHistoryParams) {
  const filterHistoryRef = useRef<FilterClause[][]>([]);
  const filtersRef = useRef<FilterClause[]>(filters);
  filtersRef.current = filters;
  const { recent, addRecent } = useRecentFilters();

  useEffect(() => {
    if (filters.length > 0) addRecent(filters);
  }, [filters, addRecent]);

  const onFiltersChangeWithHistory = useCallback(
    (next: FilterClause[]) => {
      filterHistoryRef.current.push(filtersRef.current);
      if (filterHistoryRef.current.length > 50) {
        filterHistoryRef.current.shift();
      }
      onFiltersChange(next);
    },
    [onFiltersChange],
  );

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "z") {
        const prev = filterHistoryRef.current.pop();
        if (prev) {
          e.preventDefault();
          onFiltersChange(prev);
        }
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onFiltersChange]);

  return { recent, onFiltersChangeWithHistory };
}
